// ============================================================================
// CREATE CANVAS INSTANCE DIALOG
// With Industry Template Selection
// ============================================================================

import { useState, useEffect } from 'react'
import { X, Loader2, ShoppingCart, Cloud, Users, Building2, ChevronRight, ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCreateCanvasInstance, useUpdateSectionNotes, useAutoPopulateCanvas } from '@/hooks/useCanvasInstances'
import { CANVAS_TEMPLATES, getCanvasTemplate } from './canvasTemplates'
import { industryTemplatesApi, type IndustryInfo, type SectionPrefill } from '@/lib/api/client'

interface CreateCanvasInstanceDialogProps {
  templateId: string
  onClose: () => void
  onCreated: (uri: string, name: string) => void
}

// Map industry icons to components
const INDUSTRY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'shopping-cart': ShoppingCart,
  'cloud': Cloud,
  'users': Users,
  'building': Building2,
}

export function CreateCanvasInstanceDialog({
  templateId,
  onClose,
  onCreated,
}: CreateCanvasInstanceDialogProps) {
  // Step state: 'industry' | 'details'
  const [step, setStep] = useState<'industry' | 'details'>('industry')

  // Industry selection
  const [industries, setIndustries] = useState<IndustryInfo[]>([])
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(true)
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null)
  const [industryPrefills, setIndustryPrefills] = useState<Record<string, SectionPrefill> | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const createInstance = useCreateCanvasInstance()
  const updateSectionNotes = useUpdateSectionNotes()
  const autoPopulate = useAutoPopulateCanvas()

  const template = CANVAS_TEMPLATES.find((t) => t.id === templateId)

  // Load industries on mount
  useEffect(() => {
    async function loadIndustries() {
      try {
        const result = await industryTemplatesApi.getIndustries()
        // Filter to only industries that have this canvas type
        const relevantIndustries = result.industries.filter(ind =>
          ind.canvasTypes.includes(templateId)
        )
        setIndustries(relevantIndustries)
      } catch (error) {
        console.error('Failed to load industries:', error)
      } finally {
        setIsLoadingIndustries(false)
      }
    }
    loadIndustries()
  }, [templateId])

  // Load industry template when selected
  useEffect(() => {
    async function loadTemplate() {
      if (!selectedIndustry) {
        setIndustryPrefills(null)
        return
      }
      try {
        const result = await industryTemplatesApi.getTemplate(selectedIndustry, templateId)
        setIndustryPrefills(result.template.sectionPrefills)
      } catch (error) {
        console.error('Failed to load template:', error)
        setIndustryPrefills(null)
      }
    }
    loadTemplate()
  }, [selectedIndustry, templateId])

  const handleIndustrySelect = (industryKey: string | null) => {
    setSelectedIndustry(industryKey)
    setStep('details')
  }

  const handleSkipIndustry = () => {
    setSelectedIndustry(null)
    setStep('details')
  }

  const handleBack = () => {
    setStep('industry')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    try {
      // Create the canvas instance
      const uri = await createInstance.mutateAsync({
        templateId,
        name: name.trim(),
        description: description.trim() || undefined,
      })

      // Apply industry prefills if selected
      if (industryPrefills) {
        for (const [sectionId, prefill] of Object.entries(industryPrefills)) {
          if (prefill.notes) {
            await updateSectionNotes.mutateAsync({
              canvasUri: uri,
              sectionId,
              notes: prefill.notes,
            })
          }
        }
      }

      // Auto-populate canvas sections with entities from the graph
      const canvasTemplate = getCanvasTemplate(templateId)
      if (canvasTemplate) {
        const sections = canvasTemplate.sections.map(section => ({
          sectionId: section.id,
          classUris: section.classUris,
        }))

        try {
          const result = await autoPopulate.mutateAsync({ canvasUri: uri, sections })
          console.log(`Auto-populated canvas: ${result.sectionsPopulated} sections, ${result.entitiesLinked} entities linked`)
        } catch (error) {
          console.warn('Auto-populate failed, but canvas was created:', error)
        }
      }

      onCreated(uri, name.trim())
    } catch (error) {
      console.error('Failed to create canvas instance:', error)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  const selectedIndustryInfo = industries.find(i => i.industryKey === selectedIndustry)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Dialog */}
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-200">
          <h2 className="text-lg font-semibold text-text">
            {step === 'industry' ? 'Choose Industry Template' : `New ${template?.name || 'Canvas'}`}
          </h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg hover:bg-warm-100 text-text-muted hover:text-text transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'industry' ? (
          // Industry Selection Step
          <div className="px-6 py-5">
            {isLoadingIndustries ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
              </div>
            ) : industries.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-text-muted mb-4">
                  No industry templates available for this canvas type.
                </p>
                <button
                  onClick={handleSkipIndustry}
                  className="px-4 py-2 text-sm font-medium text-white bg-solution rounded-lg hover:bg-solution/90 transition-colors"
                >
                  Start Blank
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-text-muted mb-4">
                  Choose an industry to pre-fill your canvas with relevant content, or start blank.
                </p>

                <div className="grid grid-cols-1 gap-3 mb-4">
                  {industries.map((industry) => {
                    const IconComponent = INDUSTRY_ICONS[industry.industryIcon || 'building'] || Building2
                    const isSelected = selectedIndustry === industry.industryKey

                    return (
                      <button
                        key={industry.industryKey}
                        onClick={() => handleIndustrySelect(industry.industryKey)}
                        className={cn(
                          'flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all',
                          isSelected
                            ? 'border-solution bg-solution/5'
                            : 'border-warm-200 hover:border-warm-300 hover:bg-warm-50'
                        )}
                      >
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${industry.industryColor}15` }}
                        >
                          <IconComponent
                            className="w-6 h-6"
                            style={{ color: industry.industryColor || '#00b894' }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-text">{industry.industryName}</div>
                          <div className="text-sm text-text-muted truncate">
                            {industry.industryDescription}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={handleSkipIndustry}
                  className="w-full py-3 text-sm text-text-secondary hover:text-text transition-colors"
                >
                  Skip - Start with blank canvas
                </button>
              </>
            )}
          </div>
        ) : (
          // Details Step
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              {/* Industry indicator */}
              {selectedIndustryInfo && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-warm-50 border border-warm-200">
                  {(() => {
                    const IconComponent = INDUSTRY_ICONS[selectedIndustryInfo.industryIcon || 'building'] || Building2
                    return (
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${selectedIndustryInfo.industryColor}15` }}
                      >
                        <IconComponent
                          className="w-4 h-4"
                          style={{ color: selectedIndustryInfo.industryColor || '#00b894' }}
                        />
                      </div>
                    )
                  })()}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text">{selectedIndustryInfo.industryName}</div>
                    <div className="text-xs text-text-muted">Content will be pre-filled</div>
                  </div>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-xs text-solution hover:underline"
                  >
                    Change
                  </button>
                </div>
              )}

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-text mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={getPlaceholder(templateId)}
                  className="w-full px-3 py-2 text-sm border border-warm-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-solution/30 focus:border-solution transition-colors"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-text mb-1.5">
                  Description <span className="text-text-muted font-normal">(optional)</span>
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this canvas..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-warm-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-solution/30 focus:border-solution transition-colors resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-warm-200 bg-warm-50">
              <button
                type="button"
                onClick={industries.length > 0 ? handleBack : onClose}
                className="flex items-center gap-1 px-3 py-2 text-sm text-text-secondary hover:text-text transition-colors"
              >
                {industries.length > 0 && <ChevronLeft className="w-4 h-4" />}
                {industries.length > 0 ? 'Back' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={!name.trim() || createInstance.isPending}
                className={cn(
                  'flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors',
                  name.trim() && !createInstance.isPending
                    ? 'bg-solution text-white hover:bg-solution/90'
                    : 'bg-warm-200 text-text-muted cursor-not-allowed'
                )}
              >
                {createInstance.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {createInstance.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function getPlaceholder(templateId: string): string {
  switch (templateId) {
    case 'business-model-canvas':
      return 'e.g., Enterprise SaaS Model'
    case 'value-proposition-canvas':
      return 'e.g., SMB Value Proposition'
    case 'positioning-canvas':
      return 'e.g., Market Positioning Q1'
    case 'problem-statement-canvas':
      return 'e.g., Onboarding Friction'
    default:
      return 'e.g., My Canvas'
  }
}
